import { 
  Facebook, 
  Instagram, 
  Mail, 
  Phone, 
  MapPin,
  Heart,
  Send,
  ArrowUp,
  Star,
  Sparkles,
  Globe
} from 'lucide-react';
import { useState, useEffect } from 'react';

const Footer = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  // Show scroll to top button when user scrolls down
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Facebook, href: '#', color: 'blue', label: 'فيسبوك' },
    { icon: Instagram, href: 'https://www.instagram.com/iqraaa_2025?igsh=MXNkOXliNW1qdzZzMw==', color: 'pink', label: 'إنستغرام' }
  ];

  const quickLinks = [
    { label: 'مكتبة الكتب', href: '/books' },
    { label: 'التصنيفات', href: '/categories' },
    { label: 'الكتّاب', href: '/authors' },
    { label: 'الإصدارات الجديدة', href: '/new-releases' },
    { label: 'الأكثر مبيعاً', href: '/bestsellers' },
    { label: 'العروض الخاصة', href: '/offers' }
  ];

  const supportLinks = [
    { label: 'مركز المساعدة', href: '/help' },
    { label: 'اتصل بنا', href: '/contact' },
    { label: 'الشحن والتوصيل', href: '/shipping' },
    { label: 'الإرجاع والاستبدال', href: '/returns' },
    { label: 'من نحن', href: '/about' },
    { label: 'الأسئلة الشائعة', href: '/faq' }
  ];

  return (
    <>
      <footer className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden" dir="rtl">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 right-10 w-32 h-32 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-24 h-24 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-purple-500 rounded-full blur-2xl"></div>
        </div>

        {/* Decorative Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-400 to-transparent rounded-full transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-blue-400 to-transparent rounded-full transform -translate-x-48 translate-y-48"></div>
        </div>

        {/* Main Footer Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Top Section with Newsletter */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              <span>انضم إلى مجتمع القراء</span>
              <Sparkles className="w-4 h-4" />
            </div>
            
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              كن أول من يعرف بأحدث الكتب
            </h2>
            
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto text-lg">
              احصل على التحديثات الأسبوعية للكتب الجديدة والعروض الخاصة والمحتوى الحصري
            </p>

            {/* WhatsApp Channel Subscription Button */}
            <div className="max-w-lg mx-auto">
              <button
                onClick={() => window.open('https://whatsapp.com/channel/0029Vb6Wjiq4dTnQcH6Pvf0a', '_blank')}
                className="w-full px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-medium hover:from-orange-600 hover:to-amber-600 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Send className="w-5 h-5" />
                اشترك في قناة الواتساب
              </button>
              <p className="text-gray-300 mt-4 text-sm">
                سيتم توجيهك إلى قناة الواتساب الخاصة بنا للحصول على أحدث الكتب والعروض.
              </p>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <picture>
                  {/* WebP format for modern browsers - smallest file size */}
                  <source 
                    srcSet="/logo-64.webp 1x, /logo-128.webp 2x" 
                    type="image/webp"
                  />
                  {/* PNG fallback for older browsers */}
                  <source 
                    srcSet="/logo-64.png 1x, /logo-128.png 2x" 
                    type="image/png"
                  />
                  {/* Default fallback */}
                  <img
                    src="/logo-64.png"
                    alt="Logo"
                    className="w-24 h-24 rounded-lg object-contain brightness-0 invert"
                    width="96"
                    height="96"
                    loading="lazy"
                  />
                </picture>
              </div>
              
              <p className="text-gray-300 mb-8 leading-relaxed">
                مكتبتك الإلكترونية المفضلة في سوريا. نقدم أفضل الكتب العربية والمترجمة بأسعار مناسبة لجميع القراء السوريين.
              </p>
              
              {/* Enhanced Contact Info */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-gray-300 hover:text-orange-400 transition-colors group">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
                    <Phone className="w-5 h-5" />
                  </div>
                  <span style={{ direction: 'ltr' }}>+963 934 980 444</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300 hover:text-orange-400 transition-colors group">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
                    <Mail className="w-5 h-5" />
                  </div>
                  <span>info@iqraa.cloud</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300 hover:text-orange-400 transition-colors group">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <span>حمص، سوريا</span>
                </div>
              </div>
            </div>
            {/* Quick Links */}
            <div>
              <h3 className="text-xl font-bold mb-6 text-white">روابط سريعة</h3>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="text-gray-300 hover:text-orange-400 transition-colors duration-200 flex items-center gap-2 group">
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Customer Service */}
            <div>
              <h3 className="text-xl font-bold mb-6 text-white">خدمة العملاء</h3>
              <ul className="space-y-3">
                {supportLinks.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="text-gray-300 hover:text-orange-400 transition-colors duration-200 flex items-center gap-2 group">
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Social Media & Stats */}
            <div>
              <h3 className="text-xl font-bold mb-6 text-white">تابعنا</h3>
              
              {/* Social Media Links */}
              <div className="flex flex-wrap gap-3 mb-8">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={index}
                      href={social.href}
                      className={`w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-${social.color}-500/20 transition-all duration-300 hover:scale-110 hover:shadow-lg group`}
                      aria-label={social.label}
                    >
                      <Icon className={`w-5 h-5 text-gray-300 group-hover:text-${social.color}-400 transition-colors`} />
                    </a>
                  );
                })}
              </div>

              {/* Stats */}
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <h4 className="text-lg font-semibold mb-4 text-white">إحصائيات المكتبة</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">الكتب المتوفرة</span>
                    <span className="font-bold text-orange-400" style={{ direction: 'ltr' }}>+10000</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">العملاء الراضون</span>
                    <span className="font-bold text-blue-400" style={{ direction: 'ltr' }}>+5000</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">التقييم</span>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="font-bold text-yellow-400" style={{ direction: 'ltr' }}>4.8</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 mb-8"></div>

          {/* Bottom Bar */}
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
            <div className="flex flex-col sm:flex-row items-center gap-4 text-gray-300">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500 animate-pulse" />
                <span>صُنع بحب في سوريا</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-400" />
                <span>خدمة على مدار الساعة</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center gap-4 text-sm">
              <span className="text-gray-400">© {currentYear} مكتبتي - جميع الحقوق محفوظة</span>
              <div className="flex items-center gap-4">
                <a href="/privacy" className="text-gray-400 hover:text-orange-400 transition-colors">
                  سياسة الخصوصية
                </a>
                <a href="/terms" className="text-gray-400 hover:text-orange-400 transition-colors">
                  الشروط والأحكام
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500"></div>
      </footer>

      {/* Enhanced Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 left-8 w-14 h-14 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 z-50 flex items-center justify-center group"
          aria-label="العودة للأعلى"
        >
          <ArrowUp className="w-6 h-6 group-hover:animate-bounce" />
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-amber-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        </button>
      )}

      {/* Custom Styles */}
      <style>{`
        .shadow-3xl {
          box-shadow: 0 35px 60px -12px rgba(0, 0, 0, 0.25);
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default Footer;